// src/core/data/useWorkerShifts.js

import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";

import { db } from "../../firebase";

import {
  normalizeId,
} from "../../security/permissions";

/*
 * =========================================================
 * SHIFTBRIDGE — CANONICAL WORKER SHIFT DATA CONTRACT
 * =========================================================
 *
 * PURPOSE
 * -------
 *
 * This hook is the worker-safe read boundary for /shifts.
 *
 * WorkerDashboard must NOT read an entire facility schedule
 * and filter unauthorized shifts in React.
 *
 * Firestore rules are not filters.
 *
 * Instead, every query below describes one specific reason
 * the authenticated worker is allowed to see a shift:
 *
 * 1. Direct assignment
 * 2. Legacy assignment array
 * 3. Worker request
 * 4. Direct offer
 * 5. Open canonical pool shift
 * 6. Time-required canonical pool shift
 *
 *
 * ARCHITECTURE
 * ------------
 *
 * shifts/{shiftId}
 *       │
 *       ├── assignedWorkerUid == workerUid
 *       ├── assignedWorkerUids contains workerUid
 *       ├── requestedWorkerUids contains workerUid
 *       ├── offeredWorkerUids contains workerUid
 *       │
 *       └── facility pool visibility
 *              tenantId
 *              facilityId
 *              mode == pool
 *              status == open/time_required
 *
 *       ↓
 *
 * useWorkerShifts()
 *
 *       ↓
 *
 * Worker 360
 * Worker Dashboard
 * Shift 360
 * Future allocation / offer automation
 *
 *
 * SCALE
 * -----
 *
 * This hook never subscribes to:
 *
 *   "all workers"
 *   "all facility shifts"
 *   "all tenant shifts"
 *
 * It subscribes only to records relevant to one worker.
 *
 * That keeps the Worker-facing real-time model usable even
 * when ShiftBridge contains thousands of workers.
 */

/* =========================================================
   HELPERS
   ========================================================= */

function normalizeUid(value) {
  return String(
    value || ""
  ).trim();
}

function toMillis(value) {
  if (!value) {
    return 0;
  }

  if (
    typeof value?.toDate ===
    "function"
  ) {
    try {
      return value
        .toDate()
        .getTime();
    } catch {
      return 0;
    }
  }

  if (
    typeof value?.seconds ===
    "number"
  ) {
    return (
      value.seconds * 1000
    );
  }

  const result =
    new Date(value)
      .getTime();

  return Number.isFinite(
    result
  )
    ? result
    : 0;
}

function normalizeShiftDoc(
  snapshotDoc
) {
  return {
    id: snapshotDoc.id,
    ...snapshotDoc.data(),
  };
}

function isArchivedShift(
  shift = {}
) {
  return (
    shift?.archived === true ||
    normalizeId(
      shift?.status
    ) === "archived"
  );
}

function sortShifts(
  rows = []
) {
  return [...rows].sort(
    (a, b) => {
      const aStart =
        toMillis(
          a?.startTime ||
            a?.start
        );

      const bStart =
        toMillis(
          b?.startTime ||
            b?.start
        );

      if (
        aStart !== bStart
      ) {
        return (
          aStart - bStart
        );
      }

      return String(
        a?.id || ""
      ).localeCompare(
        String(
          b?.id || ""
        )
      );
    }
  );
}

function mergeShiftGroups(
  ...groups
) {
  const byId =
    new Map();

  for (
    const rows of groups
  ) {
    for (
      const row of
      Array.isArray(rows)
        ? rows
        : []
    ) {
      if (
        !row ||
        !row.id ||
        isArchivedShift(row)
      ) {
        continue;
      }

      byId.set(
        row.id,
        row
      );
    }
  }

  return sortShifts(
    Array.from(
      byId.values()
    )
  );
}

function firstError(
  errors = {}
) {
  for (
    const value of
    Object.values(errors)
  ) {
    if (value) {
      return value;
    }
  }

  return null;
}

/* =========================================================
   MAIN HOOK
   ========================================================= */

export default function useWorkerShifts({
  tenantId = "",
  facilityId = "",
  workerUid = "",
  enabled = true,
  debugLabel =
    "WORKER SHIFTS",
} = {}) {
  const [authority, setAuthority] = useState(null);
  useEffect(() => {
    setAuthority(null);
    if (!enabled || !workerUid) return undefined;
    return onSnapshot(doc(db, "accessProfiles", workerUid), snap => {
      setAuthority(snap.exists() ? snap.data() : null);
    }, () => setAuthority(null));
  }, [enabled, workerUid]);
  const authorizedFacilities = useMemo(() => {
    if (!authority || authority.active !== true || authority.disabled === true || authority.archived === true ||
        (authority.status || "active") !== "active" || authority.tenantId !== tenantId) return [];
    return [...new Set([authority.facilityId, authority.primaryFacilityId,
      ...(Array.isArray(authority.facilityIds) ? authority.facilityIds : []),
      ...(Array.isArray(authority.allowedFacilityIds) ? authority.allowedFacilityIds : [])
    ].filter(id => typeof id === "string" && id && id !== "all"))];
  }, [authority, tenantId]);

  const normalizedTenantId =
    useMemo(
      () =>
        normalizeId(
          tenantId
        ),
      [tenantId]
    );

  const normalizedFacilityId =
    useMemo(
      () =>
        normalizeId(
          facilityId
        ),
      [facilityId]
    );

  const normalizedWorkerUid =
    useMemo(
      () =>
        normalizeUid(
          workerUid
        ),
      [workerUid]
    );

  /*
   * =======================================================
   * PUBLIC DATA
   * =======================================================
   */

  const [
    assignedShifts,
    setAssignedShifts,
  ] = useState([]);

  const [
    requestedShifts,
    setRequestedShifts,
  ] = useState([]);

  const [
    offeredShifts,
    setOfferedShifts,
  ] = useState([]);

  const [
    poolShifts,
    setPoolShifts,
  ] = useState([]);

  const [
    myShifts,
    setMyShifts,
  ] = useState([]);

  const [
    visibleShifts,
    setVisibleShifts,
  ] = useState([]);

  /*
   * =======================================================
   * HEALTH / DIAGNOSTICS
   * =======================================================
   */

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState(null);

  const [
    errors,
    setErrors,
  ] = useState({});

  const [
    channelHealth,
    setChannelHealth,
  ] = useState({});

  /* =======================================================
     LISTENERS
     ======================================================= */

  useEffect(() => {
    /* =====================================================
       DISABLED
       ===================================================== */

    if (!enabled || !authorizedFacilities.length) {
      setAssignedShifts([]);
      setRequestedShifts([]);
      setOfferedShifts([]);
      setPoolShifts([]);
      setMyShifts([]);
      setVisibleShifts([]);

      setLoading(false);
      setError(null);
      setErrors({});
      setChannelHealth({});

      return undefined;
    }

    /* =====================================================
       REQUIRED WORKER IDENTITY
       ===================================================== */

    if (
      !normalizedTenantId ||
      !normalizedWorkerUid
    ) {
      const scopeError =
        new Error(
          "Worker shift data requires tenantId and workerUid."
        );

      console.warn(
        `${debugLabel} NOT STARTED — worker scope incomplete`,
        {
          tenantId:
            normalizedTenantId,

          facilityId:
            normalizedFacilityId,

          workerUid:
            normalizedWorkerUid,
        }
      );

      setAssignedShifts([]);
      setRequestedShifts([]);
      setOfferedShifts([]);
      setPoolShifts([]);
      setMyShifts([]);
      setVisibleShifts([]);

      setLoading(false);
      setError(scopeError);

      setErrors({
        scope:
          scopeError,
      });

      setChannelHealth({
        scope: {
          ready: false,
          count: 0,
          error:
            scopeError.message,
        },
      });

      return undefined;
    }

    let active = true;

    const unsubscribers =
      [];

    /*
     * Local channel storage.
     *
     * Each listener updates only its own channel.
     * publish() then creates the canonical merged
     * Worker 360 projections.
     */
    const rowsByChannel = {};

    const errorsByChannel = {};

    const settledChannels =
      new Set();

    /* =====================================================
       QUERY CONTRACTS
       ===================================================== */

    const shiftCollection =
      collection(
        db,
        "shifts"
      );

    const channels = authorizedFacilities.flatMap(scopeId => [
      ["assignedDirect", "assignedWorkerUid", "=="],
      ["assignedArray", "assignedWorkerUids", "array-contains"],
      ["requested", "requestedWorkerUids", "array-contains"],
      ["offers", "offeredWorkerUids", "array-contains"],
    ].map(([group, field, operator]) => ({
      key: group + ":" + scopeId,
      group,
      query: query(shiftCollection, where("tenantId", "==", normalizedTenantId),
        where("facilityId", "==", scopeId), where(field, operator, normalizedWorkerUid)),
    })));

    /*
     * =====================================================
     * FACILITY POOL
     * =====================================================
     *
     * A worker may see facility pool demand only when a
     * canonical facility is known.
     *
     * IMPORTANT:
     *
     * Do NOT issue the old query:
     *
     *   tenantId == X
     *   facilityId == Y
     *
     * by itself.
     *
     * That could potentially return another worker's
     * assigned shift.
     *
     * These queries explicitly constrain:
     *
     *   mode == "pool"
     *   status == "open" / "time_required"
     *
     * which matches the Firestore worker-read contract.
     */

    if (
      normalizedFacilityId && authorizedFacilities.includes(normalizedFacilityId)
    ) {
      channels.push(
        {
          key:
            "poolOpen",

          query:
            query(
              shiftCollection,

              where(
                "tenantId",
                "==",
                normalizedTenantId
              ),

              where(
                "facilityId",
                "==",
                normalizedFacilityId
              ),

              where(
                "mode",
                "==",
                "pool"
              ),

              where(
                "status",
                "==",
                "open"
              )
            ),
        },

        {
          key:
            "poolTimeRequired",

          query:
            query(
              shiftCollection,

              where(
                "tenantId",
                "==",
                normalizedTenantId
              ),

              where(
                "facilityId",
                "==",
                normalizedFacilityId
              ),

              where(
                "mode",
                "==",
                "pool"
              ),

              where(
                "status",
                "==",
                "time_required"
              )
            ),
        }
      );
    }

    for (
      const channel of
      channels
    ) {
      rowsByChannel[
        channel.key
      ] = [];

      errorsByChannel[
        channel.key
      ] = null;
    }

    /* =====================================================
       PUBLISH CANONICAL PROJECTIONS
       ===================================================== */

    function publish() {
      if (!active) {
        return;
      }

      const rowsFor = (...groups) => channels.filter(channel => groups.includes(channel.group || channel.key))
        .map(channel => rowsByChannel[channel.key]);
      const assigned = mergeShiftGroups(...rowsFor("assignedDirect", "assignedArray"));
      const requested = mergeShiftGroups(...rowsFor("requested"));
      const offers = mergeShiftGroups(...rowsFor("offers"));
      const pool = mergeShiftGroups(...rowsFor("poolOpen", "poolTimeRequired"));

      /*
       * Existing WorkerDashboard semantics:
       *
       * myShifts =
       *   assignment + requested
       *
       * Offers remain their own projection.
       */
      const mine =
        mergeShiftGroups(
          assigned,
          requested
        );

      /*
       * Complete Worker-visible Shift 360 universe.
       *
       * Useful for:
       *
       * - Worker 360
       * - overlap checking
       * - future worker offer center
       * - future allocation feedback
       */
      const visible =
        mergeShiftGroups(
          mine,
          offers,
          pool
        );

      const nextErrors = {
        ...errorsByChannel,
      };

      const nextHealth = {};

      for (
        const channel of
        channels
      ) {
        const channelRows =
          rowsByChannel[
            channel.key
          ] || [];

        const channelError =
          errorsByChannel[
            channel.key
          ];

        nextHealth[
          channel.key
        ] = {
          ready:
            settledChannels.has(
              channel.key
            ),

          count:
            channelRows.length,

          error:
            channelError
              ? (
                  channelError
                    ?.message ||
                  String(
                    channelError
                  )
                )
              : "",
        };
      }

      setAssignedShifts(
        assigned
      );

      setRequestedShifts(
        requested
      );

      setOfferedShifts(
        offers
      );

      setPoolShifts(
        pool
      );

      setMyShifts(
        mine
      );

      setVisibleShifts(
        visible
      );

      setErrors(
        nextErrors
      );

      setError(
        firstError(
          nextErrors
        )
      );

      setChannelHealth(
        nextHealth
      );

      setLoading(
        settledChannels.size <
          channels.length
      );
    }

    /* =====================================================
       START LISTENERS
       ===================================================== */

    // Drop data from the previous scope before opening replacement listeners.
    setAssignedShifts([]);
    setRequestedShifts([]);
    setOfferedShifts([]);
    setPoolShifts([]);
    setMyShifts([]);
    setVisibleShifts([]);
    setLoading(true);
    setError(null);
    setErrors({});

    console.log(
      `${debugLabel} START`,
      {
        tenantId:
          normalizedTenantId,

        facilityId:
          normalizedFacilityId,

        workerUid:
          normalizedWorkerUid,

        channels:
          channels.map(
            (channel) =>
              channel.key
          ),
      }
    );

    for (
      const channel of
      channels
    ) {
      const unsubscribe =
        onSnapshot(
          channel.query,

          (snapshot) => {
            if (!active) {
              return;
            }

            rowsByChannel[
              channel.key
            ] =
              snapshot.docs
                .map(
                  normalizeShiftDoc
                )
                .filter(
                  (
                    shift
                  ) =>
                    !isArchivedShift(
                      shift
                    )
                );

            errorsByChannel[
              channel.key
            ] = null;

            settledChannels.add(
              channel.key
            );

            console.log(
              `${debugLabel} CHANNEL SUCCESS`,
              {
                channel:
                  channel.key,

                count:
                  rowsByChannel[
                    channel.key
                  ].length,

                tenantId:
                  normalizedTenantId,

                facilityId:
                  normalizedFacilityId,

                workerUid:
                  normalizedWorkerUid,
              }
            );

            publish();
          },

          (
            listenerError
          ) => {
            if (!active) {
              return;
            }

            rowsByChannel[
              channel.key
            ] = [];

            errorsByChannel[
              channel.key
            ] =
              listenerError;

            settledChannels.add(
              channel.key
            );

            console.error(
              `${debugLabel} CHANNEL ERROR`,
              {
                channel:
                  channel.key,

                tenantId:
                  normalizedTenantId,

                facilityId:
                  normalizedFacilityId,

                workerUid:
                  normalizedWorkerUid,

                code:
                  listenerError
                    ?.code,

                message:
                  listenerError
                    ?.message,

                error:
                  listenerError,
              }
            );

            /*
             * One failed data channel must not erase data
             * successfully received from the other channels.
             *
             * This is important for:
             *
             * - real-time resilience
             * - diagnosing missing indexes
             * - future offer automation
             */
            publish();
          }
        );

      unsubscribers.push(
        unsubscribe
      );
    }

    /* =====================================================
       CLEANUP
       ===================================================== */

    return () => {
      active = false;

      for (
        const unsubscribe of
        unsubscribers
      ) {
        try {
          unsubscribe();
        } catch (
          cleanupError
        ) {
          console.warn(
            `${debugLabel} LISTENER CLEANUP ERROR`,
            cleanupError
          );
        }
      }

      console.log(
        `${debugLabel} STOP`,
        {
          tenantId:
            normalizedTenantId,

          facilityId:
            normalizedFacilityId,

          workerUid:
            normalizedWorkerUid,
        }
      );
    };
  }, [
    authorizedFacilities,
    normalizedTenantId,
    normalizedFacilityId,
    normalizedWorkerUid,
    enabled,
    debugLabel,
  ]);

  /* =======================================================
     AUTOMATION-READY SUMMARY
     ======================================================= */

  const summary =
    useMemo(
      () => ({
        assigned:
          assignedShifts.length,

        requested:
          requestedShifts.length,

        offered:
          offeredShifts.length,

        pool:
          poolShifts.length,

        visible:
          visibleShifts.length,

        /*
         * This is useful later for:
         *
         * Worker 360 availability
         * Allocation Engine
         * Offer orchestration
         */
        hasActiveWork:
          assignedShifts.length >
            0,

        hasPendingOffers:
          offeredShifts.length >
            0,

        hasPendingRequests:
          requestedShifts.length >
            0,
      }),
      [
        assignedShifts,
        requestedShifts,
        offeredShifts,
        poolShifts,
        visibleShifts,
      ]
    );

  /* =======================================================
     RETURN CONTRACT
     ======================================================= */

  return {
    /*
     * Canonical projections
     */
    assignedShifts,
    requestedShifts,
    offeredShifts,
    poolShifts,

    /*
     * Compatibility projection for the current
     * WorkerDashboard migration.
     */
    myShifts,

    /*
     * All shifts this worker is currently authorized
     * to know about.
     */
    visibleShifts,

    /*
     * Automation / Worker 360 summary.
     */
    summary,

    /*
     * Listener health.
     */
    loading,
    error,
    errors,
    channelHealth,

    /*
     * Resolved scope.
     */
    tenantId:
      normalizedTenantId,

    facilityId:
      normalizedFacilityId,

    workerUid:
      normalizedWorkerUid,
  };
}
